import { TimestampDisplay } from '@/libs/components/src/lib/TimestampDisplay';
import { ChiroUpJSON } from '@/libs/core/src/lib/functions/ChiroUpJSON';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { Autocomplete } from '@chiroup/components/form/Autocomplete';
import { Input } from '@chiroup/components/form/Input';
import { Toggle } from '@chiroup/components/form/Toggle';
import { TrivialTooltip } from '@chiroup/components/TrivialTooltip';
import { ColumnDef, DataTable } from '@chiroup/components/ui/data-table';
import {
  DispositionChangeData,
  QueueDisposition,
  UnroutedExchangeSummary,
  UnroutedRoutingDiagnosticEntry,
} from '@chiroup/core/types/ConnectorAdmin.type';
import { exchangeDispositionDataSourceLabel } from '@chiroup/core/types/Integration.type';
import { useEffect, useMemo, useState } from 'react';
import useClinics from '../../hooks/useClinics';
import { useUnroutedQueue } from '../../hooks/useConnectionApi';
import connectionApiService from '../../services/connectionApi.service';
import { AUTOFILL_OFF } from './autofillOff';
import { connectorAssignmentRefusal, connectorErrorMessage } from './connectorErrorMessage';

const money = (n: number | null) => (n === null ? '' : n.toFixed(2));

const clinicIdsText = (clinicIds: number[]) =>
  clinicIds.length ? clinicIds.map(clinicId => `#${clinicId}`).join(', ') : 'No matches';

const selectedClinicResult = ({
  evaluated,
  clinicId,
  matchedClinicIds,
}: {
  evaluated: boolean;
  clinicId: number | null;
  matchedClinicIds: number[];
}) => {
  if (!evaluated) return 'Not evaluated';
  if (clinicId === null) return '—';

  return matchedClinicIds.includes(clinicId) ? 'Confirmed' : 'Not confirmed';
};

const RoutingDiagnostics = ({ entry }: { entry: UnroutedRoutingDiagnosticEntry }) => {
  const diagnostics = entry.diagnostics;
  const clinicId = diagnostics.clinicId;
  const controlClinicIds = [
    ...new Set(diagnostics.controlNumbers.values.flatMap(value => value.clinicIds)),
  ];
  const correlationClinicIds = [
    ...new Set(diagnostics.correlation.values.flatMap(value => value.clinicIds)),
  ];
  const payeeClinicIds = [...new Set(diagnostics.payee.matches.map(match => match.clinicId))];
  const clinicConfirmation = diagnostics.clinicConfirmation;
  const clinicConfirmationText =
    clinicConfirmation.status === 'confirmed'
      ? `Confirmed clinic #${clinicConfirmation.clinicId}`
      : clinicConfirmation.status === 'contradiction'
        ? `Confirms clinic #${clinicConfirmation.clinicId}`
        : clinicConfirmation.status === 'ambiguous'
          ? `${clinicConfirmation.matches ?? 0} possible matches`
          : clinicConfirmation.status === 'not-confirmed'
            ? 'No confirmation'
            : clinicConfirmation.status === 'not-provided'
              ? 'Not supplied'
              : 'Not evaluated';
  const clinicConfirmationResult =
    clinicId === null
      ? '—'
      : clinicConfirmation.status === 'confirmed' && clinicConfirmation.clinicId === clinicId
        ? 'Confirmed'
        : clinicConfirmation.status === 'not-evaluated'
          ? 'Not evaluated'
          : 'Not confirmed';

  return (
    <div className="overflow-x-auto rounded border border-gray-200 bg-gray-50 p-3 text-xs">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-semibold">Why this document is Unrouted</span>
        <span className="text-gray-500">
          <TimestampDisplay ts={entry.ts} format="YYYY-MM-DD HH:mm:ss" /> by {entry.by}
        </span>
      </div>

      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-gray-200 text-gray-500">
            <th className="py-1 pr-4 font-medium">Signal</th>
            <th className="py-1 pr-4 font-medium">Document value</th>
            <th className="py-1 pr-4 font-medium">Clinic matches</th>
            <th className="py-1 font-medium">
              {clinicId === null ? 'Result' : `Clinic ${clinicId}`}
            </th>
          </tr>
        </thead>
        <tbody className="align-top">
          <tr className="border-b border-gray-200">
            <td className="py-1 pr-4 font-medium">Control number</td>
            <td className="py-1 pr-4 font-mono">
              {diagnostics.controlNumbers.values.length
                ? diagnostics.controlNumbers.values.map(value => value.value).join(', ')
                : diagnostics.controlNumbers.invalidValues.length
                  ? `Invalid: ${diagnostics.controlNumbers.invalidValues.join(', ')}`
                  : 'Not supplied'}
              {diagnostics.controlNumbers.invalidValues.length
                ? diagnostics.controlNumbers.values.length
                  ? ` (invalid: ${diagnostics.controlNumbers.invalidValues.join(', ')})`
                  : ''
                : ''}
            </td>
            <td className="py-1 pr-4 font-mono">
              {diagnostics.controlNumbers.values.length
                ? diagnostics.controlNumbers.values
                    .map(value => `${value.value} → ${clinicIdsText(value.clinicIds)}`)
                    .join('; ')
                : '—'}
            </td>
            <td className="py-1">
              {selectedClinicResult({
                evaluated: diagnostics.controlNumbers.evaluated,
                clinicId,
                matchedClinicIds: controlClinicIds,
              })}
            </td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-1 pr-4 font-medium">Submission correlation</td>
            <td className="py-1 pr-4 font-mono">
              {diagnostics.correlation.values.length
                ? diagnostics.correlation.values.map(value => value.value).join(', ')
                : 'Not supplied'}
            </td>
            <td className="py-1 pr-4 font-mono">
              {!diagnostics.correlation.evaluated
                ? 'Not evaluated'
                : diagnostics.correlation.values.length
                  ? diagnostics.correlation.values
                      .map(value => `${value.value} → ${clinicIdsText(value.clinicIds)}`)
                      .join('; ')
                  : '—'}
            </td>
            <td className="py-1">
              {selectedClinicResult({
                evaluated: diagnostics.correlation.evaluated,
                clinicId,
                matchedClinicIds: correlationClinicIds,
              })}
            </td>
          </tr>
          <tr className="border-b border-gray-200">
            <td className="py-1 pr-4 font-medium">Payee</td>
            <td className="py-1 pr-4 font-mono">
              {[
                diagnostics.payee.npi ? `NPI ${diagnostics.payee.npi}` : '',
                diagnostics.payee.tin ? `TIN ${diagnostics.payee.tin}` : '',
              ]
                .filter(Boolean)
                .join(' · ') || 'Not supplied'}
            </td>
            <td className="py-1 pr-4 font-mono">
              {!diagnostics.payee.evaluated
                ? 'Not evaluated'
                : diagnostics.payee.matches.length
                  ? diagnostics.payee.matches
                      .map(
                        match => `#${match.clinicId} (billing profile #${match.billingProfileId})`
                      )
                      .join(', ')
                  : 'No matches'}
            </td>
            <td className="py-1">
              {selectedClinicResult({
                evaluated: diagnostics.payee.evaluated,
                clinicId,
                matchedClinicIds: payeeClinicIds,
              })}
            </td>
          </tr>
          <tr>
            <td className="py-1 pr-4 font-medium">Clinic confirmation</td>
            <td className="py-1 pr-4">
              {clinicConfirmation.status === 'not-provided'
                ? 'Not supplied'
                : clinicConfirmation.status === 'not-evaluated'
                  ? 'Not evaluated'
                  : 'Document reference supplied'}
            </td>
            <td className="py-1 pr-4">{clinicConfirmationText}</td>
            <td className="py-1">{clinicConfirmationResult}</td>
          </tr>
        </tbody>
      </table>

      {diagnostics.gate && clinicId !== null ? (
        <p className="mt-2 border-t border-gray-200 pt-2 text-gray-600">
          Clinic {clinicId}: {diagnostics.gate.strongSignalCount} strong signal
          {diagnostics.gate.strongSignalCount === 1 ? '' : 's'} matched. Confirmation requires
          submission correlation, two agreeing strong signals, or one strong signal plus clinic
          confirmation.
        </p>
      ) : null}
    </div>
  );
};

/**
 * Columns for the queue list. The list renders denormalized `meta` display
 * fields only — never the payload (contract guarantee). Money right-aligns
 * (feedback_numbers_right_align); the "Matched clinics" column surfaces the
 * candidates the funnel resolved so the router picks an informed clinicId.
 */
const columns: ColumnDef<UnroutedExchangeSummary, unknown>[] = [
  {
    accessorKey: 'receivedAt',
    header: 'Received',
    cell: ({ row }) => (
      // skipCleanupDatetime: the default pipeline runs `.replace(/:00/g, '')` as sugar
      // for 'h:mma' formats (3:00pm → 3pm), which silently eats the seconds of any
      // timestamp that lands on the minute — 16:46:00 rendered as '16:46', so rows
      // looked like they had different precision from each other.
      <TimestampDisplay
        ts={row.original.receivedAt}
        format="YYYY-MM-DD HH:mm:ss"
        skipCleanupDatetime
      />
    ),
    enableSorting: false,
    meta: { className: 'whitespace-nowrap font-mono' },
  },
  {
    accessorKey: 'vendor',
    header: 'Vendor',
    cell: ({ row }) => row.original.vendor ?? '',
    enableSorting: false,
    meta: { className: 'whitespace-nowrap' },
  },
  {
    accessorKey: 'docType',
    header: 'Doc',
    cell: ({ row }) => row.original.docType ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  { accessorKey: 'reason', header: 'Reason', enableSorting: false },
  {
    accessorKey: 'payorName',
    header: 'Payor',
    cell: ({ row }) => row.original.payorName ?? '',
    enableSorting: false,
  },
  {
    accessorKey: 'payeeNpi',
    header: 'Payee NPI',
    cell: ({ row }) => row.original.payeeNpi ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  {
    accessorKey: 'payeeTin',
    header: 'TIN',
    cell: ({ row }) => row.original.payeeTin ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  {
    accessorKey: 'trn',
    header: 'TRN/Check No.',
    cell: ({ row }) => row.original.trn ?? '',
    enableSorting: false,
    meta: { className: 'font-mono' },
  },
  {
    accessorKey: 'paymentAmount',
    header: 'Amount',
    cell: ({ row }) => money(row.original.paymentAmount),
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums font-mono' },
  },
  {
    accessorKey: 'clpCount',
    header: 'CLPs',
    cell: ({ row }) => row.original.clpCount ?? '',
    enableSorting: false,
    meta: { align: 'right' as const, className: 'tabular-nums' },
  },
  {
    id: 'candidates',
    header: 'Matched clinics',
    enableSorting: false,
    cell: ({ row }) => {
      const candidates = row.original.candidates ?? [];

      if (!candidates.length) return <span className="text-gray-400">—</span>;

      return (
        <div className="space-y-0.5">
          {candidates.map(c => (
            <div
              key={c.clinicId}
              className={`whitespace-nowrap ${c.connectorParticipant ? '' : 'text-gray-400'}`}
            >
              <span className="font-mono">{c.clinicId}</span>
              {c.clinicName ? ` · ${c.clinicName}` : ''}
              {c.connectorParticipant ? '' : ' — not set up'}
            </div>
          ))}
        </div>
      );
    },
  },
];

/**
 * Unrouted-document queue (plan §3e.7 / INFRA-5): inbound documents whose
 * clinic could not be determined park here (Unrouted disposition, clinicId 0)
 * — never silently dropped, never guessed. A human routes each row: assign a
 * clinic (re-runs the ingest funnel with clinicId pinned; corroboration still
 * enforced) or close it with a reason. Both are audited server-side.
 *
 * The list renders denormalized `meta` display fields only — never the payload.
 * When the funnel matched candidate clinics for an ambiguous document, they are
 * shown so the assign pick is informed rather than a guess.
 */
/** The close/reopen audit trail for the selected row. */
const HistoryTrail = ({ history }: { history: UnroutedExchangeSummary['history'] }) => {
  if (!history.length) return null;

  return (
    <ul className="space-y-0.5 text-xs text-gray-600">
      {history.map((h, i) => {
        const d = (h.data ?? {}) as DispositionChangeData;

        return (
          <li key={i}>
            <TimestampDisplay ts={d.at} format="YYYY-MM-DD HH:mm" />
            {` — ${d.from} → ${d.to} by ${d.by}: ${h.text}`}
            {d.src ? ` (${exchangeDispositionDataSourceLabel(d.src)})` : ''}
          </li>
        );
      })}
    </ul>
  );
};

export const Queues = () => {
  // Unsupported shares this surface because clinicId 0 has no other home, but it is a
  // DIFFERENT question: Unrouted needs a clinic from a human, Unsupported needs code.
  // Only the visible queue is fetched (item 4) — `view` IS the query.
  const [view, setView] = useState<QueueDisposition>('unrouted');
  const queue = useUnroutedQueue(view);
  const { assign, close, reopen, reprocess } = queue;
  const [sweeping, setSweeping] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [assignClinicId, setAssignClinicId] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [reopenMessage, setReopenMessage] = useState('');
  const [pendingAction, setPendingAction] = useState<'assign' | 'close' | 'reopen' | null>(null);
  const [stamping, setStamping] = useState(false);
  const [stampedId, setStampedId] = useState<string | null>(null);

  const viewRows = queue.data?.rows ?? [];
  const totals = queue.data?.totals;
  const selected: UnroutedExchangeSummary | null =
    viewRows.find(r => r.exchangeId === selectedId) ?? null;
  const assignmentRefusal = connectorAssignmentRefusal(assign.error);
  const routingDiagnostic = assignmentRefusal?.details ?? selected?.routingDiagnostics?.[0] ?? null;
  const resolveOnly = assignmentRefusal !== null;

  // A refusal belongs to the row/action it answered (item 3) — clear mutation
  // errors when the selection or view moves on, so row A's refusal can never
  // render against row B.
  useEffect(() => {
    assign.reset();
    close.reset();
    reopen.reset();
    reprocess.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, view]);

  const switchView = (next: QueueDisposition) => {
    setView(next);
    setSelectedId(null);
    setAssignClinicId('');
    setCloseReason('');
    setReopenMessage('');
  };

  /**
   * Drain every queued document of one type. Reports what actually happened —
   * `remaining > 0` means the cap was hit and it needs running again, which the
   * operator must know rather than assume the queue emptied.
   */
  const sweepDocType = async (docType: string) => {
    setSweeping(true);

    try {
      const result = await connectionApiService.reprocessDocType(docType);

      window.alert(
        `${docType}: ${result.processed} posted, ${result.unrouted} need a clinic, ` +
          `${result.failed.length} failed. ${result.remaining} still queued.`
      );
      await queue.refetch();
      setSelectedId(null);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : `Could not reprocess ${docType}.`);
    } finally {
      setSweeping(false);
    }
  };

  // Trace toggle: stamp the exchange so the assign re-run leaves breadcrumbs in
  // the ops console (on = 15 min; off = un-stamp via durationMinutes 0).
  const setTraceStamp = async (on: boolean) => {
    if (!selected) return;

    setStamping(true);

    try {
      await connectionApiService.stampExchange(selected.exchangeId, on ? 15 : 0);

      setStampedId(on ? selected.exchangeId : null);
    } catch {
      window.alert(`Could not ${on ? 'enable' : 'disable'} tracing for '${selected.exchangeId}'.`);
    } finally {
      setStamping(false);
    }
  };

  const clinicIdNum = Number(assignClinicId.trim());
  const canAssign = !!selected && Number.isInteger(clinicIdNum) && clinicIdNum > 0;

  // Clinic-name typeahead so a worker who doesn't know the clinicId can find it
  // and fill the box. Lazy (skipEmptySearch) — no query until they type.
  const {
    data: clinicsData,
    isFetching: isFetchingClinics,
    fetchNextPage: fetchNextClinics,
    hasNextPage: hasNextClinics,
    onSearch: onSearchClinics,
    searchQuery: clinicSearchQuery,
  } = useClinics({ skipEmptySearch: true });

  const clinicOptions = useMemo(
    () =>
      (clinicsData?.pages?.flatMap(p => p?.data ?? []) ?? [])
        .filter(Boolean)
        .map(c => ({ text: `${c.name} (#${c.ID})`, value: c.ID })),
    [clinicsData?.pages]
  );

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          Queues{' '}
          <span className="text-base font-normal text-gray-500">
            {totals
              ? `${totals.unrouted} unrouted · ${totals.unsupported} unsupported · ` +
                `${totals.resolved} resolved`
              : '…'}
          </span>
        </h1>
      </div>

      <div className="flex gap-8">
        <Button
          size="sm"
          color={view === 'unrouted' ? ButtonColors.primary : ButtonColors.plain}
          onClick={() => switchView('unrouted')}
          text="Unrouted"
          badge={totals?.unrouted ?? 0}
        />
        <Button
          size="sm"
          color={view === 'unsupported' ? ButtonColors.primary : ButtonColors.plain}
          onClick={() => switchView('unsupported')}
          text="Unsupported"
          badge={totals?.unsupported ?? 0}
        />
        <Button
          size="sm"
          color={view === 'resolved' ? ButtonColors.primary : ButtonColors.plain}
          onClick={() => switchView('resolved')}
          text="Resolved"
          badge={totals?.resolved ?? 0}
        />
      </div>

      {queue.isError ? (
        <p className="text-sm text-red-600">
          Could not load the queue — the endpoint is unreachable.
        </p>
      ) : (
        <div className="[&_td]:align-top!">
          <DataTable
            columns={columns}
            data={viewRows}
            size="compact"
            loading={queue.isLoading}
            onRowClick={r => setSelectedId(r.exchangeId)}
            rowClassName={row =>
              `cursor-pointer ${row.original.exchangeId === selectedId ? 'bg-blue-50' : ''}`
            }
            pagination={{ pageSize: 50, pageSizeOptions: [50, 100] }}
            emptyState={
              <p className="text-sm text-gray-500">
                {view === 'unrouted'
                  ? 'No open documents — every inbound document routed to a clinic.'
                  : view === 'unsupported'
                    ? 'Nothing waiting on code — every document received has a parser and a handler.'
                    : 'No resolved documents.'}
              </p>
            }
          />
          {totals && viewRows.length < totals[view] ? (
            <p className="mt-1 text-xs text-gray-500">
              Showing the newest {viewRows.length} of {totals[view]} {view} documents.
            </p>
          ) : null}
        </div>
      )}

      {selected ? (
        <div className="mt-4 space-y-3 border-t pt-4">
          <div className="text-sm">
            <span className="font-mono">{selected.exchangeId}</span>
          </div>

          <HistoryTrail history={selected.history} />

          {selected.disposition === 'unrouted' && routingDiagnostic ? (
            <RoutingDiagnostics entry={routingDiagnostic} />
          ) : null}

          {selected.disposition === 'unsupported' ? (
            // Nothing for a human to supply here — this row waits on CODE. Reprocess
            // pushes it back through the funnel; the server re-checks support, so a
            // stale button cannot force a document through.
            <div className="space-y-3">
              {/*
                What the document says it IS. Two readings side by side because they can
                disagree — the vendor's label versus what the bytes parse as — and the
                disagreement is the interesting case. 'UNK' means we could not MAP it,
                not that we know nothing about it.
              */}
              {selected.descriptors ? (
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="rounded bg-gray-50 p-2">
                    <div className="mb-1 font-medium">Vendor says</div>
                    <pre className="whitespace-pre-wrap break-all">
                      {ChiroUpJSON.pretty(selected.descriptors.vendor ?? {})}
                    </pre>
                  </div>
                  <div className="rounded bg-gray-50 p-2">
                    <div className="mb-1 font-medium">Bytes say (X12 sniff)</div>
                    <pre className="whitespace-pre-wrap break-all">
                      {ChiroUpJSON.pretty(selected.descriptors.sniffed ?? {})}
                    </pre>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  color={ButtonColors.primary}
                  loading={reprocess.isLoading}
                  disabled={!selected.reprocessable}
                  tooltip={
                    selected.reprocessable
                      ? `Push this ${selected.docType ?? 'document'} back through routing and posting.`
                      : `No parser/handler for ${selected.docType ?? 'this type'} yet — it stays ` +
                        `queued until support ships. Nothing is lost.`
                  }
                  onClick={() => reprocess.mutate({ exchangeId: selected.exchangeId })}
                  text="Reprocess"
                />
                {selected.docType && selected.reprocessable ? (
                  <Button
                    color={ButtonColors.plain}
                    loading={sweeping}
                    onClick={() => sweepDocType(selected.docType as string)}
                    text={`Reprocess all ${selected.docType}`}
                    tooltip="Drain every queued document of this type — the post-deploy sweep."
                  />
                ) : null}
                <Button
                  color={ButtonColors.plain}
                  onClick={() => setSelectedId(null)}
                  text="Cancel"
                />
              </div>
              {reprocess.isError ? (
                <p className="text-sm text-red-600">
                  {connectorErrorMessage(reprocess.error, 'Reprocess failed.')}
                </p>
              ) : null}

              {/*
                Some documents are simply junk — garbled, foreign, a transaction set
                nobody will implement. Closing with a reason moves it to Resolved so it
                stops appearing here, and the reason is REQUIRED because a row that
                vanishes without one is indistinguishable from a bug. Reversible: reopen
                restores it to THIS queue, not the Unrouted one.
              */}
              <div className="flex flex-wrap items-end gap-2 border-t pt-3">
                <div className="w-96">
                  <Input
                    name="unsupported-close-reason"
                    label="Not worth handling — reason (required)"
                    value={closeReason}
                    onChange={(val: string) => setCloseReason(val)}
                    {...AUTOFILL_OFF}
                  />
                </div>
                <Button
                  color={ButtonColors.plain}
                  loading={close.isLoading}
                  disabled={!closeReason.trim()}
                  onClick={() => setPendingAction('close')}
                  text="Dismiss"
                  tooltip="Stop showing this document. Recorded with your reason, and reversible."
                />
              </div>
              {close.isError ? (
                <p className="text-sm text-red-600">
                  {connectorErrorMessage(close.error, 'Dismiss failed.')}
                </p>
              ) : null}
            </div>
          ) : selected.disposition === 'resolved' ? (
            // Resolved rows can only be REOPENED (back to Unrouted) — clinicId 0
            // has no other surface, so the queue is where a reversed decision lands.
            <div className="space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="w-96">
                  <Input
                    name="unrouted-reopen-message"
                    label="Reopen reason (required)"
                    value={reopenMessage}
                    onChange={(val: string) => setReopenMessage(val)}
                    {...AUTOFILL_OFF}
                  />
                </div>
                <Button
                  color={ButtonColors.primary}
                  loading={reopen.isLoading}
                  disabled={!reopenMessage.trim()}
                  onClick={() => setPendingAction('reopen')}
                  text="Reopen"
                />
                <Button
                  color={ButtonColors.plain}
                  onClick={() => {
                    setSelectedId(null);
                    setReopenMessage('');
                  }}
                  text="Cancel"
                />
              </div>
              {reopen.isError ? (
                <p className="text-sm text-red-600">
                  {connectorErrorMessage(reopen.error, 'Reopen failed.')}
                </p>
              ) : null}
            </div>
          ) : (
            <>
              {!resolveOnly && selected.candidates.length ? (
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-xs text-gray-500">Matched clinics — click to assign:</span>
                  {selected.candidates.map(c => (
                    <Button
                      key={c.clinicId}
                      size="sm"
                      color={ButtonColors.plain}
                      // Shown either way, but a clinic with no api Integration row for
                      // this vendor cannot own connector traffic (the endpoint enforces it too).
                      disabled={!c.connectorParticipant}
                      tooltip={
                        c.connectorParticipant
                          ? undefined
                          : `Clinic ${c.clinicId} is not set up to receive ${
                              selected.vendor ?? 'this vendor'
                            } — it can't be assigned this document.`
                      }
                      onClick={() => setAssignClinicId(String(c.clinicId))}
                      text={
                        `${c.clinicId}` +
                        (c.clinicName ? ` · ${c.clinicName}` : '') +
                        (c.npi ? ` (NPI ${c.npi})` : '') +
                        (c.connectorParticipant ? '' : ' — not set up')
                      }
                    />
                  ))}
                </div>
              ) : null}

              {!resolveOnly ? (
                <div className="w-96">
                  <Autocomplete
                    name="unrouted-clinic-search"
                    label="Don't know the id? Find a clinic by name"
                    className="w-full"
                    // Controlled by the same id the box holds, so picking sticks
                    // (a constant value never registers the selection in HeadlessUI).
                    value={assignClinicId ? Number(assignClinicId) : ''}
                    limit={1}
                    clearable
                    options={clinicOptions}
                    isFetching={isFetchingClinics}
                    fetchNextPage={fetchNextClinics}
                    hasNextPage={hasNextClinics}
                    onChangeSearch={onSearchClinics}
                    searchTerm={clinicSearchQuery}
                    serverSide
                    placeholder="Search clinics…"
                    onChange={val => setAssignClinicId(val ? String(val) : '')}
                  />
                </div>
              ) : null}

              <div className="flex flex-wrap items-end gap-2">
                {!resolveOnly ? (
                  <>
                    <div className="flex flex-col">
                      <span className="block text-sm font-medium leading-5 text-gray-900 dark:text-dark-gray-200">
                        Trace
                        <TrivialTooltip
                          text="Stamp this exchange for 15 minutes of tracing before you assign — the re-run's steps then show up in the ops console trace log."
                          type="info"
                        />
                      </span>
                      <div className="flex h-9.5 items-center">
                        <Toggle
                          value={stampedId === selected.exchangeId}
                          disabled={stamping}
                          onChange={on => setTraceStamp(on)}
                        />
                      </div>
                    </div>
                    <div className="w-40">
                      <Input
                        name="unrouted-clinic-id"
                        label="Assign to clinic id"
                        value={assignClinicId}
                        onChange={(val: string) => setAssignClinicId(val)}
                        {...AUTOFILL_OFF}
                      />
                    </div>
                    <Button
                      color={ButtonColors.primary}
                      loading={assign.isLoading}
                      disabled={!canAssign}
                      onClick={() => setPendingAction('assign')}
                      text="Assign"
                    />
                  </>
                ) : null}
                <div className="w-96">
                  <Input
                    name="unrouted-resolve-reason"
                    label="Resolve reason (required)"
                    value={closeReason}
                    onChange={(val: string) => setCloseReason(val)}
                    {...AUTOFILL_OFF}
                  />
                </div>
                <Button
                  color={ButtonColors.red}
                  loading={close.isLoading}
                  disabled={!closeReason.trim()}
                  onClick={() => setPendingAction('close')}
                  text="Resolve"
                />
                <Button
                  color={ButtonColors.plain}
                  onClick={() => {
                    setSelectedId(null);
                    setAssignClinicId('');
                    setCloseReason('');
                  }}
                  text="Cancel"
                />
              </div>
              {assign.isError && !resolveOnly ? (
                <p className="text-sm text-red-600">
                  {connectorErrorMessage(assign.error, 'Assign failed.')}
                </p>
              ) : null}
              {close.isError ? (
                <p className="text-sm text-red-600">
                  {connectorErrorMessage(close.error, 'Resolve failed.')}
                </p>
              ) : null}
            </>
          )}
        </div>
      ) : viewRows.length ? (
        <p className="mt-3 text-xs text-gray-500">
          {view === 'unrouted'
            ? 'Select a row to assign or close it.'
            : view === 'unsupported'
              ? 'Select a row to see what it is, reprocess it, or dismiss it.'
              : 'Select a row to reopen it.'}
        </p>
      ) : null}

      <ConfirmModal
        isOpen={!!pendingAction}
        close={() => setPendingAction(null)}
        title={
          pendingAction === 'assign'
            ? 'Assign this document?'
            : pendingAction === 'close'
              ? selected?.disposition === 'unsupported'
                ? 'Dismiss this document?'
                : 'Resolve this document?'
              : 'Reopen this document?'
        }
        description={
          !selected
            ? undefined
            : pendingAction === 'assign'
              ? `Assign ${selected.exchangeId} to clinic ${clinicIdNum} and re-run routing? Corroboration still applies — a clinic the document doesn't corroborate to is refused and it stays queued.`
              : pendingAction === 'close'
                ? selected.disposition === 'unsupported'
                  ? `Stop showing ${selected.exchangeId}? The document is kept and stays auditable — it moves to the Resolved tab, and reopen returns it to the Unsupported queue.`
                  : `Resolve ${selected.exchangeId} without routing it? It moves to the Resolved tab (reversible via reopen).`
                : `Reopen ${selected.exchangeId} back into the queue?`
        }
        confirmText={
          pendingAction === 'assign'
            ? 'Assign'
            : pendingAction === 'close'
              ? selected?.disposition === 'unsupported'
                ? 'Dismiss'
                : 'Resolve'
              : 'Reopen'
        }
        isDelete={pendingAction === 'close'}
        confirm={() => {
          if (selected && pendingAction === 'assign') {
            assign.mutate(
              { exchangeId: selected.exchangeId, clinicId: clinicIdNum },
              { onSuccess: () => setAssignClinicId('') }
            );
          } else if (selected && pendingAction === 'close') {
            close.mutate(
              { exchangeId: selected.exchangeId, reason: closeReason.trim() },
              { onSuccess: () => setCloseReason('') }
            );
          } else if (selected && pendingAction === 'reopen') {
            reopen.mutate(
              { exchangeId: selected.exchangeId, message: reopenMessage.trim() },
              { onSuccess: () => setReopenMessage('') }
            );
          }

          setPendingAction(null);
        }}
      />
    </div>
  );
};

export default Queues;
